import React, { useEffect, useRef } from "react";
import { Check, ChevronDown } from "lucide-react";
import type { TimezoneFilter } from "./types";
import { useLobTimezone } from "../../../hooks/useLobTimezone";

interface TimezoneDropdownProps {
  value: TimezoneFilter;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: (value: TimezoneFilter) => void;
}

export const TimezoneDropdown: React.FC<TimezoneDropdownProps> = ({ value, open, setOpen, onChange }) => {
  const lobTimezone = useLobTimezone();
  const options: { code: TimezoneFilter; label: string }[] = [
    { code: lobTimezone.code, label: lobTimezone.label },
    { code: "UTC", label: "UTC" },
  ];
  const labelFor = (code: TimezoneFilter) => options.find((o) => o.code === code)?.label ?? code;
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, setOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
      <button
        type="button"
        className="w-full min-h-[52px] flex items-center justify-between px-3 py-2 border border-gray-200 rounded-md text-sm bg-white shadow-sm hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
        onClick={() => setOpen((curr) => !curr)}
      >
        <div className="text-left">
          <p className="text-sm font-medium text-gray-800">{labelFor(value)}</p>
          <p className="text-xs text-gray-500">Date range timezone</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg p-1">
          {options.map(({ code: tz, label }) => (
            <button
              key={tz}
              type="button"
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm hover:bg-gray-50 ${
                value === tz ? "font-semibold bg-gray-100" : ""
              }`}
              onClick={() => {
                onChange(tz);
                setOpen(false);
              }}
            >
              <span
                className={`w-4 h-4 rounded border flex items-center justify-center ${
                  value === tz ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
                }`}
              >
                {value === tz && <Check className="w-3 h-3 text-white" />}
              </span>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
